"use client";

import Image from "next/image";
import { useState, useRef, useEffect } from "react";
import { createPortal } from "react-dom";

export default function PaymentTooltip({ method, className, width, height }) {
    const hasValidSrc = typeof method?.src === "string" && method.src.trim() !== "";
    const methodName = method?.name || "Payment method";
    const [visible, setVisible] = useState(false);
    const timerRef = useRef(null);
    const wrapperRef = useRef(null);
    const [rect, setRect] = useState(null);

    // Update bounds if hovered and page is scrolled (or if mobile taps)
    const updateRect = () => {
        if (wrapperRef.current) {
            setRect(wrapperRef.current.getBoundingClientRect());
        }
    };

    useEffect(() => {
        if (visible) {
            updateRect();
            window.addEventListener("scroll", updateRect, true);
            window.addEventListener("resize", updateRect);
            return () => {
                window.removeEventListener("scroll", updateRect, true);
                window.removeEventListener("resize", updateRect);
            };
        }
    }, [visible]);

    // Close tooltip when clicking outside
    useEffect(() => {
        function handleClickOutside(e) {
            if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
                setVisible(false);
            }
        }
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    function handleMouseEnter() {
        updateRect();
        clearTimeout(timerRef.current);
        setVisible(true);
    }

    function handleMouseLeave() {
        timerRef.current = setTimeout(() => setVisible(false), 150);
    }

    function handleTap(e) {
        e.preventDefault();
        if (!visible) updateRect();
        setVisible((prev) => !prev);
    }

    if (!hasValidSrc) {
        return null;
    }

    return (
        <div
            ref={wrapperRef}
            className="relative inline-flex shrink-0 items-center"
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
            onClick={handleTap}
        >
            <Image
                src={method.src}
                alt={methodName}
                width={width}
                height={height}
                className={`${className} cursor-pointer transition-transform duration-150 hover:scale-[1.03] object-cover`}
                loading="lazy"
            />

            {/* 
				Portal to escape the marquee's `overflow: hidden` constraint!
				The 3-layer architecture separates the coordinate positioning from
				the animation logic, completely fixing any CSS transform collisions ("blinking").
			*/}
            {visible &&
                typeof document !== "undefined" &&
                createPortal(
                    <div
                        className="fixed z-50 pointer-events-none"
                        style={{
                            left: rect ? rect.left + rect.width / 2 : 0,
                            top: rect ? rect.top - 8 : 0,
                        }}
                    >
                        {/* Coordinate Centering Layer */}
                        <div className="absolute bottom-0 left-1/2 -translate-x-1/2">
                            {/* Animation Data Layer */}
                            <div
                                className="whitespace-nowrap rounded-md bg-primary-dark px-2.5 py-1 text-xs font-semibold text-white shadow-md animate-in fade-in duration-200"
                                style={{ animation: "fade-up 0.15s ease-out forwards" }}
                            >
                                {methodName}
                                <div
                                    className="absolute left-1/2 top-full -translate-x-1/2 border-[5px] border-transparent"
                                    style={{ borderTopColor: "var(--primary-dark)" }}
                                />
                            </div>
                        </div>
                    </div>,
                    document.body
                )}
        </div>
    );
}
